import React, { useMemo, useState } from 'react';
import {
  Button,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Select,
  Text,
  useToast,
} from '@chakra-ui/react';
import { FaDownload, FaArrowLeft, FaCog } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { getData } from '../../../utils/httpClient';
import AdminPageShell from '../../../components/layout/AdminPageShell';
import { primaryButtonProps, ghostButtonProps } from '../../../theme/adminUi';
import { cloneServiceByModel, downloadSbsExcel, rebuildSbsMetrics, rebuildServiceByModel } from './sbsExcel';
import SbsEditablePreview from './SbsEditablePreview';

const MONTH_OPTIONS = [
  { value: '1', label: 'Enero' },
  { value: '2', label: 'Febrero' },
  { value: '3', label: 'Marzo' },
  { value: '4', label: 'Abril' },
  { value: '5', label: 'Mayo' },
  { value: '6', label: 'Junio' },
  { value: '7', label: 'Julio' },
  { value: '8', label: 'Agosto' },
  { value: '9', label: 'Septiembre' },
  { value: '10', label: 'Octubre' },
  { value: '11', label: 'Noviembre' },
  { value: '12', label: 'Diciembre' },
];

export default function SbsReport() {
  const toast = useToast();
  const navigate = useNavigate();
  const now = new Date();
  const [year, setYear] = useState(String(now.getFullYear()));
  const [month, setMonth] = useState(String(now.getMonth() + 1));
  const [payload, setPayload] = useState(null);
  const [editedMetrics, setEditedMetrics] = useState(null);
  const [editedServiceByModel, setEditedServiceByModel] = useState(null);
  const [omittedSbsRows, setOmittedSbsRows] = useState([]);
  const [serviceLines, setServiceLines] = useState([]);
  const [excludedServiceIds, setExcludedServiceIds] = useState([]);
  const [removedModels, setRemovedModels] = useState([]);
  const [modelTemplate, setModelTemplate] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [hasPreviewed, setHasPreviewed] = useState(false);

  const monthLabel = useMemo(
    () => MONTH_OPTIONS.find((m) => m.value === month)?.label || '',
    [month],
  );

  const resetPreview = () => {
    setHasPreviewed(false);
    setPayload(null);
    setEditedMetrics(null);
    setEditedServiceByModel(null);
    setOmittedSbsRows([]);
    setServiceLines([]);
    setExcludedServiceIds([]);
    setRemovedModels([]);
    setModelTemplate([]);
  };

  const load = async () => {
    const reportYear = Number(year);
    const reportMonth = Number(month);
    if (!Number.isFinite(reportYear) || reportYear < 2000) {
      toast({
        title: 'Año inválido',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    if (!Number.isFinite(reportMonth) || reportMonth < 1 || reportMonth > 12) {
      toast({
        title: 'Mes inválido',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setLoading(true);
    try {
      const res = await getData(
        `/reports/sbs?year=${encodeURIComponent(reportYear)}&month=${encodeURIComponent(reportMonth)}`,
      );
      const data = res?.payload || null;
      const lines = data?.services || [];
      const template = cloneServiceByModel(data?.serviceByModel || []);
      setPayload(data);
      setServiceLines(lines);
      setExcludedServiceIds([]);
      setRemovedModels([]);
      setModelTemplate(template);
      setEditedMetrics(data?.metrics ? { ...data.metrics } : null);
      setEditedServiceByModel(template);
      setOmittedSbsRows([]);
      setHasPreviewed(true);
    } catch (err) {
      toast({
        title: 'Error al cargar reporte SBS',
        description: err?.response?.data?.message || err?.message || 'Intentá nuevamente.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
      setPayload(null);
      setEditedMetrics(null);
      setEditedServiceByModel(null);
      setHasPreviewed(true);
    } finally {
      setLoading(false);
    }
  };

  const downloadExcel = async () => {
    const reportYear = Number(year);
    const reportMonth = Number(month);
    setDownloading(true);
    try {
      let data = payload;
      let metrics = editedMetrics;
      let serviceByModel = editedServiceByModel;

      if (!data?.metrics) {
        const res = await getData(
          `/reports/sbs?year=${encodeURIComponent(reportYear)}&month=${encodeURIComponent(reportMonth)}`,
        );
        data = res?.payload;
        metrics = data?.metrics ? { ...data.metrics } : null;
        serviceByModel = cloneServiceByModel(data?.serviceByModel || []);
        setPayload(data);
        setEditedMetrics(metrics);
        setEditedServiceByModel(serviceByModel);
        setHasPreviewed(true);
      }

      if (!metrics) {
        toast({
          title: 'Sin datos',
          description: 'Generá la vista previa antes de descargar.',
          status: 'info',
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      downloadSbsExcel(
        {
          ...data,
          metrics,
          serviceByModel: serviceByModel || [],
          omittedSbsRowNums: omittedSbsRows,
        },
        reportYear,
        reportMonth,
      );
    } catch (err) {
      toast({
        title: 'Error al descargar Excel',
        description: err?.response?.data?.message || err?.message || 'Intentá nuevamente.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <AdminPageShell
      title="Reporte SBS"
      actions={
        <HStack spacing={2}>
          <Button
            {...ghostButtonProps}
            leftIcon={<FaArrowLeft />}
            onClick={() => navigate('/admin/reports')}
          >
            Volver
          </Button>
          <Button
            {...ghostButtonProps}
            leftIcon={<FaCog />}
            onClick={() => navigate('/admin/reports/sbs-config')}
          >
            Configuración
          </Button>
          <Button
            {...primaryButtonProps}
            leftIcon={<FaDownload />}
            onClick={downloadExcel}
            isLoading={downloading}
          >
            Descargar Excel
          </Button>
        </HStack>
      }
    >
      <Text fontSize="sm" color="gray.600" mb={4}>
        Reporte mensual de servicios Volkswagen. Generá la vista previa con los
        datos del sistema. En Servicios realizados podés quitar un servicio y se
        actualizan la hoja SBS y el cruce por modelo. También podés editar
        valores o sacar filas antes de descargar.
      </Text>

      <HStack spacing={3} align="flex-end" mb={6} flexWrap="wrap">
        <FormControl maxW="160px">
          <FormLabel fontSize="sm">Año</FormLabel>
          <Input
            type="number"
            value={year}
            onChange={(e) => {
              setYear(e.target.value);
              resetPreview();
            }}
            min={2000}
            max={2100}
          />
        </FormControl>
        <FormControl maxW="200px">
          <FormLabel fontSize="sm">Mes</FormLabel>
          <Select
            value={month}
            onChange={(e) => {
              setMonth(e.target.value);
              resetPreview();
            }}
          >
            {MONTH_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
        </FormControl>
        <Button {...primaryButtonProps} onClick={load} isLoading={loading}>
          Generar vista previa
        </Button>
      </HStack>

      {hasPreviewed && editedMetrics && (
        <SbsEditablePreview
          metrics={editedMetrics}
          serviceByModel={editedServiceByModel || []}
          services={serviceLines.filter(
            (line) => !excludedServiceIds.includes(line.id),
          )}
          monthLabel={`${monthLabel} ${year}`}
          omittedSbsRows={omittedSbsRows}
          onMetricsChange={setEditedMetrics}
          onServiceByModelChange={setEditedServiceByModel}
          onRemoveSbsRow={(rowNum) =>
            setOmittedSbsRows((prev) =>
              prev.includes(rowNum) ? prev : [...prev, rowNum],
            )
          }
          onRemoveModelRow={(rowIndex) => {
            setEditedServiceByModel((prev) => {
              const model = prev?.[rowIndex]?.model;
              if (model) {
                setRemovedModels((current) =>
                  current.includes(model) ? current : [...current, model],
                );
              }
              return (prev || []).filter((_, index) => index !== rowIndex);
            });
          }}
          onRemoveService={(serviceId) => {
            const nextExcluded = excludedServiceIds.includes(serviceId)
              ? excludedServiceIds
              : [...excludedServiceIds, serviceId];
            const remaining = serviceLines.filter(
              (line) => !nextExcluded.includes(line.id),
            );
            setExcludedServiceIds(nextExcluded);
            setEditedMetrics((prev) => rebuildSbsMetrics(prev || {}, remaining));
            setEditedServiceByModel(
              rebuildServiceByModel(modelTemplate, remaining).filter(
                (row) => !removedModels.includes(row.model),
              ),
            );
          }}
        />
      )}

      {hasPreviewed && !editedMetrics && (
        <Text fontSize="sm" color="gray.500">
          Sin datos para {monthLabel} {year}. Revisá la configuración SBS o
          registrá servicios en el período.
        </Text>
      )}
    </AdminPageShell>
  );
}
